import api from 'api/setupAxios';
import { MERCHANT_PLAN_CALLBACK_PATHNAME } from 'const/Const';
import {
  MERCHANT_COVER_IMAGE,
  MERCHANT_SUBCRIPTION_PLAN,
  MERCHANT_SETTING,
  MERCHANT_SLUG,
  MERCHANT_SUBSCRIPTION_CALLBACK,
  MERCHANT_UNREGISTER_SUBCRIPTION_PLAN,
  MERCHANT_UPLOAD,
  SUBSCRIPTION_PRODUCT,
  SUBSCRIPTION_FEES,
  SUBSCRIPTION_FEES_UPDATE,
  SUBSCRIPTION_FEES_PATH
} from 'const/endpoint';
import type {
  MerchantSubscriptionFeesParams,
  MerchantSubscriptionPlanParams
} from 'types/ApiParams';
import type { BaseResponseList } from 'types/general';
import type {
  Merchant,
  MerchantReq,
  MerchantSetting,
  MerchantSubscriptionPlanResponse,
  Period,
  SubscriptionProduct,
  SubscriptionPlanDetail,
  SubscriptionFees,
  MerchantSubscriptionPlan
} from 'types/Merchant';
import { generateQueryString } from 'utils';

export const getMerchantV2 = async (slug: string, signal?: AbortSignal) => {
  const res = await api
    .call(signal)
    .get<Merchant>(MERCHANT_SLUG.replace(':slug', slug));
  return res.data;
};

export const uploadImageProfileV2 = async (fd: any) => {
  const config = {
    headers: { 'content-type': 'multipart/form-data' }
  };
  const res = await api.call().post(MERCHANT_UPLOAD, fd, config);
  return res.data;
};

export const uploadImageCover = async (fd: any) => {
  const config = {
    headers: { 'content-type': 'multipart/form-data' }
  };
  const res = await api.call().post(MERCHANT_COVER_IMAGE, fd, config);
  return res.data;
};

export const updateMerchantInfoV2 = async (slug: string, data: MerchantReq) => {
  const config = {
    headers: { 'Content-Type': 'application/merge-patch+json' }
  };
  const res = await api
    .call()
    .patch(MERCHANT_SLUG.replace(':slug', slug), data, config);
  return res.data;
};

export const updateMerchantDescription = async (
  slug: string,
  data: { description?: string; isFinishSetup?: boolean }
) => {
  const res = await api.call().put(MERCHANT_SLUG.replace(':slug', slug), {
    ...(data.description && { description: data.description }),
    ...(data.isFinishSetup && { isFinishSetup: data.isFinishSetup })
  });

  return res.data;
};

export const getStatisticsDashboardV2 = async (
  period: Period,
  signal?: AbortSignal
) => {
  const res = await api
    .call(signal)
    .get(`merchant/statistics/dashboard?period=${period}`);
  return res.data;
};

export const updateMerchantSetting = async (
  slug: string,
  data: MerchantSetting
) => {
  const config = {
    headers: { 'Content-Type': 'application/merge-patch+json' }
  };
  const res = await api
    .call()
    .patch(MERCHANT_SETTING.replace(':slug', slug), data, config);

  return res.data;
};

export const registerMerchantSubscriptionPlan = async (origin: string) => {
  const res = await api
    .call()
    .post<MerchantSubscriptionPlanResponse>(MERCHANT_SUBCRIPTION_PLAN, {
      callbackUrl: origin + MERCHANT_PLAN_CALLBACK_PATHNAME
    });

  return res.data;
};

export const registerMerchantSubscriptionPlanWithId = async () => {
  const res = await api.call().get(MERCHANT_SUBSCRIPTION_CALLBACK);

  return res.data;
};

export const unsubscribeMerchantPlan = async (subscriptionId: number) => {
  const config = {
    headers: { 'Content-Type': 'application/merge-patch+json' }
  };
  const res = await api.call().patch(
    MERCHANT_UNREGISTER_SUBCRIPTION_PLAN.replace(
      ':id',
      subscriptionId.toString()
    ),
    {
      isCancelled: true
    },
    config
  );

  return res.data;
};

export const getMerchantSubscriptionPlans = async (
  params: MerchantSubscriptionPlanParams,
  signal?: AbortSignal
) => {
  const res = await api
    .call(signal)
    .get<BaseResponseList<SubscriptionPlanDetail>>(
      MERCHANT_SUBCRIPTION_PLAN + generateQueryString(params)
    );

  return res.data;
};

export const getMerchantSubscriptionProducts = async (signal?: AbortSignal) => {
  const res = await api
    .call(signal)
    .get<SubscriptionProduct>(SUBSCRIPTION_PRODUCT);

  return res.data;
};

export const getSubscriptionPlanDetail = async (
  id: number,
  signal?: AbortSignal
) => {
  const res = await api
    .call(signal)
    .get(MERCHANT_SUBCRIPTION_PLAN + `/${id}` + '/details');

  return res.data;
};

export const getSubscriptionFees = async (
  params: MerchantSubscriptionFeesParams,
  signal?: AbortSignal
) => {
  const res = await api
    .call(signal)
    .get<BaseResponseList<SubscriptionFees>>(
      SUBSCRIPTION_FEES + generateQueryString(params)
    );

  return res.data;
};

export const registerSubscriptionPlan = async (
  slug: string,
  data: MerchantSubscriptionPlan
) => {
  const config = {
    headers: { 'Content-Type': 'application/ld+json' }
  };

  const res = await api.call().put(
    SUBSCRIPTION_FEES_UPDATE.replace(':slug', slug),
    {
      ...(data.subscription && {
        subscription: SUBSCRIPTION_FEES_PATH.replace(
          ':id',
          data.subscription ?? ''
        )
      }),
      ...(data.subscriptionMode && { subscriptionMode: data.subscriptionMode })
    },
    config
  );

  return res.data;
};
